/* PARK shared UI source: assets/park-cart-notification.js */
/* Shared confirmation. Cart mutations stay with each existing product controller. */
(() => {
  'use strict';
  if (window.ParkCartNotification) return;
  const getDialog = () => document.querySelector('[data-park-cart-notification]');
  const pendingForms = new WeakSet();
  let returnFocus = null;
  const refresh = cart => document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', {
    bubbles: true, detail: {openDrawer: false, cart}
  }));
  function show(result, addedItems = []) {
    const dialog = getDialog();
    if (!dialog) return false;
    if (!dialog.open) {
      returnFocus = document.activeElement;
      dialog.showModal();
    }
    window.ParkCartNotificationFlow?.opened(result);
    return true;
  }
  async function addForm(form, submitter) {
    if (pendingForms.has(form)) return;
    pendingForms.add(form);
    const buttons = [...form.querySelectorAll('[type="submit"]')];
    const previous = buttons.map(button => button.disabled);
    const body = new FormData(form);
    if (submitter?.name && submitter.name !== 'add') body.append(submitter.name, submitter.value);
    const sectionRequest = window.ParkCartNotificationFlow?.sectionRequest();
    if (sectionRequest) { body.set('sections', sectionRequest.sections.join(',')); body.set('sections_url', sectionRequest.sections_url); }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    buttons.forEach(button => { button.disabled = true; });
    form.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST', signal: controller.signal, headers: {Accept: 'application/json'}, body
      });
      const result = await response.json();
      if (!response.ok || result.status >= 400) {
        const error = new Error(typeof result.description === 'string' ? result.description : getDialog()?.dataset.error);
        error.cartAPI = true;
        throw error;
      }
      show(result, [{id: body.get?.('id'), quantity: Number(body.get?.('quantity') || 1)}]);
      refresh(result);
      return result;
    } catch (error) {
      // An uncertain response may have changed the cart. Refresh, never retry.
      if (!error.cartAPI) refresh();
      throw error;
    } finally {
      clearTimeout(timeout);
      buttons.forEach((button, index) => { button.disabled = previous[index]; });
      form.removeAttribute('aria-busy');
      pendingForms.delete(form);
    }
  }
  window.ParkCartNotification = {show, addForm};
  document.addEventListener('click', event => {
    const cartLink = event.target.closest('#pageheader a.cart-link');
    if (!cartLink || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !getDialog()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    show();
  }, true);
  document.addEventListener('click', event => {
    const dialog = getDialog();
    if (!dialog?.open) return;
    const bounds = dialog.getBoundingClientRect();
    const outside = event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom);
    if (event.target.closest('[data-cart-notification-close]') || outside) dialog.close();
  });
  document.addEventListener('close', event => {
    if (!event.target.matches('[data-park-cart-notification]')) return;
    if (returnFocus?.isConnected) returnFocus.focus();
    returnFocus = null;
  }, true);
  // Legacy bundled integrations signal success through these existing events.
  document.addEventListener('theme:open-cart-drawer', () => show());
  document.addEventListener('theme:cartchanged', event => { if (event.detail?.openDrawer) show(); });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.tagName !== 'FORM' || event.defaultPrevented || !getDialog()) return;
    const action = new URL(form.action, location.origin);
    if (action.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
    // Existing controllers preserve validation, selling plans and their own errors.
    if (form.matches('.product-purchase-form') || form.closest('park-pdp, [data-park-pdp], [data-lp-bundle]')) return;
    event.preventDefault();
    let status = form.querySelector('[data-park-cart-form-status]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.parkCartFormStatus = '';
      status.setAttribute('role', 'status');
      form.appendChild(status);
    }
    status.textContent = '';
    addForm(form, event.submitter).catch(error => {
      status.textContent = error.cartAPI ? error.message : getDialog().dataset.error;
    });
  });
})();

;

/* PARK shared UI source: assets/park-cart-upsell.js */
/* Shared cart recommendation controls. Shopify owns cart rendering and prices. */
(() => {
  'use strict';
  if (customElements.get('park-cart-upsell')) return;
  let pending = false;
  class ParkCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.addEventListener('click', event => {
        const dialog = event.target.closest('[data-upsell-dialog]');
        if (dialog) {
          // This modal has its own backdrop and close control inside the cart modal.
          event.stopPropagation();
          if (event.target.closest('[data-upsell-close]') || event.target === dialog) {
            dialog.close();
            return;
          }
        }
        const choose = event.target.closest('[data-upsell-choose]');
        if (choose && (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
        if (choose && this.contains(choose)) {
          event.preventDefault();
          if (choose.disabled || pending) return;
          const picker = choose.closest('[data-upsell-product]').querySelector('[data-upsell-dialog]');
          picker.querySelectorAll('[data-upsell-variant]').forEach(input => { input.checked = false; });
          picker.querySelector('[data-upsell-add]').disabled = true;
          this.returnFocus = choose;
          picker.showModal();
          picker.querySelector('[data-upsell-variant]')?.focus();
          return;
        }
        const button = event.target.closest('[data-upsell-add]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        this.add(button);
      }, options);
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-upsell-variant]')) return;
        const dialog = event.target.closest('[data-upsell-dialog]');
        if (dialog) dialog.querySelector('[data-upsell-add]').disabled = !dialog.querySelector('[data-upsell-variant]:checked');
      }, options);
      this.addEventListener('cancel', event => {
        if (event.target.matches('[data-upsell-dialog]')) event.stopPropagation();
      }, {...options, capture: true});
      this.addEventListener('close', event => {
        if (!event.target.matches('[data-upsell-dialog]')) return;
        if (this.returnFocus?.isConnected) this.returnFocus.focus();
        this.returnFocus = null;
      }, {...options, capture: true});
    }
    disconnectedCallback() {
      this.querySelectorAll('[data-upsell-dialog][open]').forEach(dialog => dialog.close());
      this.controller?.abort();
      this.controller = null;
      this.returnFocus = null;
    }
    async add(submitter) {
      if (pending || submitter.disabled) return;
      const card = submitter.closest('[data-upsell-product]');
      const picker = submitter.closest('[data-upsell-dialog]');
      const choice = card?.querySelector('[data-upsell-variant]:checked');
      if (picker && !choice) return;
      const variantId = choice?.value || submitter.dataset.upsellAdd;
      const quantity = Number(choice?.dataset.quantity || 1);
      if (!variantId || !Number.isInteger(quantity) || quantity < 1) return;
      const button = picker ? card.querySelector('[data-upsell-choose]') : submitter;
      const standalone = this.dataset.openCart === 'true';
      const drawer = this.closest('[data-park-cart-notification]');
      const flow = window.ParkCartNotificationFlow;
      const drawerFlow = drawer ? flow : null;
      if (drawerFlow?.beginAdd?.() === false) return;
      if (picker) picker.close();
      const lifetime = this.controller;
      const active = () => this.isConnected && this.controller === lifetime;
      pending = true;
      const status = this.querySelector('[data-upsell-status]');
      const label = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = submitter.dataset.loading || status.dataset.loading;
      status.textContent = status.dataset.loading;
      let refresh = true;
      let uncertain = false, result;
      const mutationController = new AbortController();
      const mutationTimeout = setTimeout(() => mutationController.abort(), 10000);
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', signal: mutationController.signal,
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({items: [{id: variantId, quantity}], ...flow?.sectionRequest()})
        });
        result = await response.json();
        if (!response.ok || result.status >= 400) {
          const error = new Error(typeof result.description === 'string' ? result.description : status.dataset.error);
          error.cartAPI = true;
          throw error;
        }
        if (active()) {
          status.textContent = status.dataset.success;
          if (this.closest('[data-park-cart-notification]')) {
            card.classList.add('is-added');
            button.textContent = this.dataset.addedLabel;
            button.setAttribute('aria-label', this.dataset.addedLabel);
            button.disabled = true;
            delete button.dataset.drawerLocked;
            window.ParkCartNotification?.show(result, [{id: variantId, quantity}]);
          } else if (standalone) {
            window.ParkCartNotification?.show(result, [{id: variantId, quantity}]);
            status.textContent = '';
          }
        }
      } catch (error) {
        // A lost response may still have changed the cart. Never retry the mutation.
        refresh = !error.cartAPI;
        uncertain = refresh;
        if (active()) status.textContent = error.cartAPI ? error.message : status.dataset.error;
      } finally {
        clearTimeout(mutationTimeout);
        let rendered = false;
        try {
          rendered = await drawerFlow?.endAdd?.(refresh ? (uncertain ? undefined : result) : false);
        } catch (_) {
          refresh = true;
          uncertain = true;
          if (active()) status.textContent = status.dataset.error;
        } finally {
          pending = false;
        }
        if (button.isConnected) {
          button.disabled = standalone ? false : refresh;
          button.removeAttribute('aria-busy');
          if (standalone || !refresh || uncertain) button.textContent = label;
          if (refresh && !uncertain && drawer?.open && [document.body, button, submitter].includes(document.activeElement)) {
            drawer.querySelector('[data-cart-notification-close]')?.focus();
          }
        }
        if (refresh) document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', {
          bubbles: true, detail: {openDrawer: false, refreshRecommendations: uncertain, cart: uncertain ? undefined : result, cartDrawerUpdated: Boolean(rendered)}
        }));
      }
    }
  }
  customElements.define('park-cart-upsell', ParkCartUpsell);
})();

;

/* PARK shared UI source: assets/park-cart-notification-flow.js */
(() => {
  'use strict';
  if (window.parkCartFlowInstalled) return;
  window.parkCartFlowInstalled = true;
  function initialize() {
  window.ParkCartNotificationFlow?.destroy?.();
  const events = new AbortController();
  const on = (target, type, handler) => target.addEventListener(type, handler, {signal: events.signal});
  const dialog = document.querySelector('[data-park-cart-notification]');
  if (!dialog || dialog.dataset.flowReady) return;
  dialog.dataset.flowReady = 'true';
  const loading = dialog.querySelector('[data-notification-loading]');
  const error = dialog.querySelector('[data-notification-error]');
  const errorText = dialog.querySelector('[data-drawer-error-text]');
  const defaultError = errorText.textContent;
  let revision = 0, timer, activeRead, changing = false, keepRecommendations = false, freshUntil = 0;

  function lock(locked) {
    dialog.setAttribute('aria-busy', String(locked));
    const checkout = dialog.querySelector('[data-drawer-checkout]');
    if (locked) checkout?.setAttribute('aria-disabled', 'true');
    else checkout?.removeAttribute('aria-disabled');
    dialog.querySelectorAll('[data-drawer-quantity], [data-upsell-add], [data-upsell-choose]').forEach(button => {
      if (locked && !button.disabled) {
        button.dataset.drawerLocked = 'true';
        button.disabled = true;
      } else if (!locked && button.dataset.drawerLocked) {
        button.disabled = false;
        delete button.dataset.drawerLocked;
      }
    });
  }
  const renderedResults = new WeakSet();
  const selectors = ['[data-drawer-body]', '[data-drawer-footer]', '[data-drawer-count]'];
  const sectionRequest = () => {
    const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
    if (dialog.dataset.previewThemeId) url.searchParams.set('preview_theme_id', dialog.dataset.previewThemeId);
    return {sections: [dialog.dataset.contextSection], sections_url: url.pathname + url.search};
  };
  function parse(html) {
    if (typeof html !== 'string') throw new Error('Section missing');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = selectors.map(selector => doc.querySelector(selector));
    if (fresh.some(node => !node)) throw new Error('Section missing');
    return fresh;
  }
  function apply(fresh, focus) {
    const oldList = dialog.querySelector('.cc-park-cart-upsell__list');
    const newList = fresh[0].querySelector('.cc-park-cart-upsell__list');
    if (keepRecommendations && oldList && newList) newList.replaceChildren(...Array.from(oldList.children));
    const count = Number(fresh[2].dataset.cartCount);
    selectors.forEach((selector, index) => dialog.querySelector(selector).replaceChildren(...Array.from(fresh[index].childNodes)));
    if (Number.isInteger(count) && count >= 0) {
      dialog.querySelector('[data-drawer-count]').dataset.cartCount = String(count);
      window.theme?.updateCartCount?.(count);
    }
    keepRecommendations = true;
    freshUntil = Date.now() + 15000;
    loading.hidden = true; error.hidden = true; lock(false);
    // Preserved recommendations must reflect the complete server-rendered cart,
    // not the partial items returned by cart/add.js or their previous button state.
    const products = new Set(Array.from(dialog.querySelectorAll('[data-drawer-product]'), row => row.dataset.drawerProduct));
    dialog.querySelectorAll('[data-upsell-product]').forEach(card => {
      const button = card.querySelector('[data-upsell-label]');
      if (!button) return;
      const added = products.has(card.dataset.upsellProduct);
      const addedLabel = card.closest('park-cart-upsell').dataset.addedLabel;
      card.classList.toggle('is-added', added);
      button.disabled = added;
      button.textContent = added ? addedLabel : button.dataset.upsellLabel;
      button.setAttribute('aria-label', added ? addedLabel : button.dataset.upsellAccessibleLabel);
      delete button.dataset.drawerLocked;
    });
    if (focus && dialog.open) {
      const row = Array.from(dialog.querySelectorAll('[data-drawer-line]')).find(node => node.dataset.drawerLine === focus.key);
      const target = row?.querySelector(`[data-drawer-action="${focus.action}"]`);
      if (target && !target.disabled) target.focus();
      else dialog.querySelector('[data-cart-notification-close]').focus();
    }
  }
  function consume(result, focus) {
    if (events.signal.aborted) return false;
    if (result && renderedResults.has(result)) return true;
    const html = result?.sections?.[dialog.dataset.contextSection];
    if (typeof html !== 'string') return false;
    try {
      const fresh = parse(html);
      clearTimeout(timer); ++revision; activeRead?.abort();
      apply(fresh, focus);
      renderedResults.add(result);
      return true;
    } catch (_) { return false; }
  }
  // Only reads may be retried. A timed-out cart mutation may already have succeeded.
  async function readSection(url, current) {
    for (let attempt = 0; attempt < 2; attempt++) {
      const controller = new AbortController();
      activeRead = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(url, {cache: 'no-store', signal: controller.signal});
        if (!response.ok) throw new Error('Section unavailable');
        return parse(await response.text());
      } catch (failure) {
        if (current !== revision || attempt === 1) throw failure;
      } finally {
        clearTimeout(timeout);
        if (activeRead === controller) activeRead = null;
      }
    }
  }
  async function update(focus) {
    if (events.signal.aborted) return false;
    freshUntil = 0;
    const current = ++revision;
    activeRead?.abort();
    lock(true); loading.hidden = false; error.hidden = true;
    try {
      const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
      url.searchParams.set('section_id', dialog.dataset.contextSection);
      // Preview cookies can expire independently of an already-open draft page.
      // Use the rendered draft ID, never a production-theme fallback.
      const previewId = dialog.dataset.previewThemeId;
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
      const fresh = await readSection(url, current);
      if (current !== revision) return;
      apply(fresh, focus);
      return true;
    } catch (failure) {
      if (current === revision) {
        // Technical reason only: no cart contents or customer data in the log.
        console.warn('[PARK cart] Drawer refresh failed', {
          reason: failure.name === 'AbortError' ? 'timeout' :
            ['Section unavailable', 'Section missing'].includes(failure.message) ? failure.message : 'network-or-render',
          section: dialog.dataset.contextSection
        });
        errorText.textContent = defaultError;
        error.hidden = false;
      }
    } finally {
      if (current === revision) loading.hidden = true;
    }
  }
  function schedule() {
    clearTimeout(timer);
    if (!changing && !events.signal.aborted) timer = setTimeout(() => update(), 50);
  }
  on(dialog, 'close', () => { keepRecommendations = false; });
  const invalidate = () => { freshUntil = 0; };
  on(window, 'focus', invalidate);
  on(window, 'pageshow', invalidate);
  on(document, 'visibilitychange', invalidate);
  window.ParkCartNotificationFlow = {
    sectionRequest,
    beginAdd() {
      if (changing || dialog.getAttribute('aria-busy') === 'true' || events.signal.aborted) return false;
      changing = true;
      clearTimeout(timer); ++revision; activeRead?.abort(); freshUntil = 0;
      error.hidden = true; lock(true);
      return true;
    },
    async endAdd(result) {
      if (events.signal.aborted) return false;
      try {
        if (result === false) { lock(false); return true; }
        if (consume(result)) return true;
        keepRecommendations = false;
        const rendered = await update();
        if (!events.signal.aborted && !result) {
          errorText.textContent = defaultError;
          error.hidden = false;
        }
        return rendered;
      } finally { changing = false; }
    },
    isRendered: result => !!result && renderedResults.has(result),
    opened(result) { if (!consume(result) && Date.now() >= freshUntil) schedule(); },
    destroy() { events.abort(); clearTimeout(timer); ++revision; activeRead?.abort(); delete dialog.dataset.flowReady; }
  };
  on(document, 'shopify:section:unload', event => { if (event.target.contains(dialog)) window.ParkCartNotificationFlow?.destroy(); });
  on(dialog.querySelector('[data-notification-retry]'), 'click', () => {
    if (!changing) update();
  });
  on(document, 'theme:cartchanged', event => {
    if (event.detail?.cartDrawerUpdated || (event.detail?.cart && renderedResults.has(event.detail.cart))) return;
    invalidate();
    // An uncertain upsell may have been added. Let Liquid exclude current cart
    // products instead of preserving an old, possibly misleading recommendation.
    if (event.detail?.refreshRecommendations) keepRecommendations = false;
    if (dialog.open) schedule();
  });
  on(dialog, 'click', async event => {
    if (event.target.closest('[data-drawer-checkout][aria-disabled="true"]')) {
      event.preventDefault();
      return;
    }
    const button = event.target.closest('[data-drawer-quantity]');
    if (!button || button.disabled || changing) return;
    const row = button.closest('[data-drawer-line]');
    const quantity = Number(button.dataset.drawerQuantity);
    if (!row || !Number.isInteger(quantity) || quantity < 0) return;
    const focus = {key: row.dataset.drawerLine, action: button.dataset.drawerAction};
    changing = true;
    clearTimeout(timer); ++revision; activeRead?.abort();
    lock(true); loading.hidden = false; error.hidden = true;
    freshUntil = 0;
    let mutationError, cart;
    const mutationController = new AbortController();
    const mutationTimeout = setTimeout(() => mutationController.abort(), 10000);
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST', signal: mutationController.signal, headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({id: row.dataset.drawerLine, quantity, ...sectionRequest()})
      });
      cart = await response.json();
      if (!response.ok || cart.status >= 400) {
        mutationError = typeof cart.description === 'string' ? cart.description : dialog.dataset.error;
      }
    } catch (_) {
      mutationError = dialog.dataset.error;
    } finally {
      clearTimeout(mutationTimeout);
    }
    if (events.signal.aborted) {
      // The write may have succeeded, but only the current instance may render.
      document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', {bubbles: true, detail: {openDrawer: false}}));
      return;
    }
    // Read back after both confirmed and uncertain responses; never repeat a mutation.
    try {
      const rendered = (!mutationError && consume(cart, focus)) || await update(focus);
      if (events.signal.aborted) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', {bubbles: true, detail: {openDrawer: false}}));
        return;
      }
      if (mutationError) {
        errorText.textContent = mutationError;
        error.hidden = false;
      }
      document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', {
        bubbles: true, detail: {openDrawer: false, cart: mutationError ? undefined : cart, cartDrawerUpdated: Boolean(rendered)}
      }));
    } finally {
      changing = false;
    }
  });
  }
  initialize();
  document.addEventListener('shopify:section:load', event => {
    if (event.target.querySelector('[data-park-cart-notification]')) initialize();
  });
})();

;

/* PARK shared UI source: assets/park-newsletter.js */
(function(root) {
  'use strict';
  function payload(email, list, source) {
    return {data:{type:'subscription',attributes:{custom_source:source,profile:{data:{type:'profile',attributes:{email,subscriptions:{email:{marketing:{consent:'SUBSCRIBED'}}}}}}},relationships:{list:{data:{type:'list',id:list}}}}};
  }
  async function subscribe(config, email, consent, send, signal) {
    if (config.demo || !config.ready || !consent || !config.company || !config.list) throw new Error('not-ready');
    const response = await send('https://a.klaviyo.com/client/subscriptions/?company_id='+encodeURIComponent(config.company), {
      method:'POST', headers:{'content-type':'application/vnd.api+json',revision:'2026-07-15'},
      body:JSON.stringify(payload(email,config.list,config.source)), signal
    });
    if (response.status !== 202) throw new Error('not-accepted');
    return 'requested';
  }
  if (typeof module !== 'undefined' && module.exports) module.exports={payload,subscribe};
  if (!root.customElements || root.customElements.get('park-newsletter')) return;
  const editorOpen = new Set();
  class ParkNewsletter extends HTMLElement {
    connectedCallback() {
      if(this.initialized) return;
      this.initialized=true;
      this.events=new AbortController();
      const on=(target,event,handler,options={})=>target.addEventListener(event,handler,{...options,signal:this.events.signal});
      this.designMode=!!root.Shopify?.designMode;
      this.dialog=this.querySelector('dialog');this.opener=this.querySelector('[data-newsletter-open]');
      this.form=this.querySelector('form');this.button=this.form.querySelector('[type="submit"]');
      this.status=this.querySelector('[data-newsletter-status]');
      this.config={demo:this.dataset.demo==='true',ready:this.dataset.ready==='true',company:this.dataset.company,list:this.dataset.list,source:this.dataset.source};
      this.opener.hidden=false;this.button.disabled=!(this.config.ready||this.config.demo);
      on(this.opener,'click',()=>this.open(this.opener));
      on(this.querySelector('[data-newsletter-close]'),'click',()=>this.dialog.close());
      on(this.dialog,'close',()=>{if(this.designMode&&this.isConnected)editorOpen.delete(this.dataset.sectionId);this.form.querySelector('[name="email"]').value='';this.form.querySelector('[name="consent"]').checked=false;if(this.returnFocus?.isConnected)this.returnFocus.focus();});
      on(this.dialog,'click',e=>{if(e.target===this.dialog){const r=this.dialog.getBoundingClientRect();if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom)this.dialog.close();}});
      on(this.form,'submit',e=>this.submit(e));
      if(this.designMode){
        on(document,'shopify:section:select',event=>{if(event.target.contains(this))this.open();});
        on(document,'shopify:block:select',event=>{if(event.target.contains(this)||this.contains(event.target))this.open();});
        if(this.dataset.editorPreview==='true'&&editorOpen.has(this.dataset.sectionId))this.open();
      }else this.setupAutomatic(on);
    }
    open(opener) {
      if(!this.designMode&&!this.deviceAllowed())return false;
      if(this.dialog.open || document.querySelector('dialog[open],.cc-popup--visible')) return false;
      this.returnFocus=opener||document.activeElement;this.dialog.showModal();
      if(this.designMode&&this.dataset.editorPreview==='true')editorOpen.add(this.dataset.sectionId);
      this.dialog.querySelector('h2').focus();
      if(!this.designMode){this.autoShown=true;this.rememberView();this.stopAutomatic();}
      return true;
    }
    deviceAllowed() {
      const device=this.dataset.devices||'desktop';
      return device==='both'||(device==='mobile')===!!this.mobileQuery?.matches;
    }
    storageKey(){return 'park-newsletter-view:'+this.dataset.sectionId;}
    alreadyViewed() {
      try {
        if(this.dataset.autoFrequency==='page')return false;
        if(this.dataset.autoFrequency==='day')return Date.now()-Number(root.localStorage.getItem(this.storageKey())||0)<86400000;
        return root.sessionStorage.getItem(this.storageKey())==='1';
      }catch(_){return false;}
    }
    rememberView() {
      try {
        if(this.dataset.autoFrequency==='day')root.localStorage.setItem(this.storageKey(),String(Date.now()));
        else if(this.dataset.autoFrequency!=='page')root.sessionStorage.setItem(this.storageKey(),'1');
      }catch(_){}
    }
    setupAutomatic(on) {
      this.autoShown=false;this.timeReached=false;
      this.mobileQuery=root.matchMedia('(max-width:760px)');
      const syncDevice=()=>{this.opener.hidden=!this.deviceAllowed();if(this.opener.hidden&&this.dialog.open)this.dialog.close();this.tryAutomatic();};
      on(this.mobileQuery,'change',syncDevice);
      syncDevice();
      if(this.autoShown||this.alreadyViewed()||!this.dataset.autoTrigger||this.dataset.autoTrigger==='manual')return;
      if(['time','either'].includes(this.dataset.autoTrigger))this.autoTimer=root.setTimeout(()=>{this.autoTimer=null;this.timeReached=true;this.tryAutomatic();},Number(this.dataset.autoDelay||15)*1000);
      const schedule=()=>{if(this.autoShown||this.autoFrame)return;this.autoFrame=root.requestAnimationFrame(()=>{this.autoFrame=null;this.tryAutomatic();});};
      if(['scroll','either'].includes(this.dataset.autoTrigger))on(root,'scroll',schedule,{passive:true});
      on(root,'resize',schedule);
      on(document,'visibilitychange',schedule);
      on(document,'close',schedule,{capture:true});
    }
    tryAutomatic() {
      const mode=this.dataset.autoTrigger;
      if(this.designMode||this.autoShown||!mode||mode==='manual'||!this.deviceAllowed()||document.hidden||this.alreadyViewed())return;
      const maxScroll=document.documentElement.scrollHeight-root.innerHeight;
      const scrolled=maxScroll>0&&root.scrollY/maxScroll*100>=Number(this.dataset.autoScroll||50);
      const ready=mode==='time'?this.timeReached:mode==='scroll'?scrolled:this.timeReached||scrolled;
      if(!ready)return;
      const cookie=document.querySelector('#CybotCookiebotDialog');
      const blocked=document.querySelector('dialog[open],.cc-popup--visible')||(cookie&&cookie.getClientRects().length>0)||document.activeElement?.matches('input,textarea,select,[contenteditable="true"]');
      if(blocked){if(!this.autoRetry)this.autoRetry=root.setTimeout(()=>{this.autoRetry=null;this.tryAutomatic();},1000);return;}
      this.open();
    }
    stopAutomatic() {
      root.clearTimeout?.(this.autoTimer);root.clearTimeout?.(this.autoRetry);root.cancelAnimationFrame?.(this.autoFrame);
      this.autoTimer=null;this.autoRetry=null;this.autoFrame=null;
    }
    setStatus(key) {
      this.status.textContent = this.dataset[key];
      const width = Number(this.dataset[key + 'Width']);
      this.status.style.maxWidth = width > 0 ? `min(100%, ${width}px)` : '';
    }
    async submit(event) {
      event.preventDefault();
      if(this.designMode)return;
      if(this.pending||(!this.config.ready&&!this.config.demo)||!this.form.reportValidity())return;
      const email=this.form.querySelector('[name="email"]').value.trim();
      const consent=this.form.querySelector('[name="consent"]').checked;
      if(!consent)return;
      if(this.config.demo){
        this.setStatus('demoSuccess');
        this.form.querySelector('[name="email"]').value='';
        this.form.querySelector('[name="consent"]').checked=false;
        this.status.focus();return;
      }
      this.pending=true;this.button.disabled=true;this.form.setAttribute('aria-busy','true');
      const labelNodes=[...this.button.childNodes];this.button.textContent=this.dataset.loading;
      const loadingWidth=Number(this.dataset.loadingWidth);
      if(loadingWidth>0){const span=document.createElement('span');span.style.display='inline-block';span.style.maxWidth=`min(100%, ${loadingWidth}px)`;span.textContent=this.dataset.loading;this.button.replaceChildren(span);}
      this.status.textContent='';this.controller=new AbortController();
      const timer=setTimeout(()=>this.controller.abort(),15000);
      try {
        await subscribe(this.config,email,consent,root.fetch.bind(root),this.controller.signal);
        this.setStatus('success');this.form.querySelector('[name="email"]').value='';
        this.form.querySelector('[name="consent"]').checked=false;this.config.ready=false;
      } catch (_) {this.setStatus('error');}
      finally {clearTimeout(timer);this.pending=false;this.form.removeAttribute('aria-busy');this.button.replaceChildren(...labelNodes);this.button.disabled=!this.config.ready;if(this.dialog.open)this.status.focus();}
    }
    disconnectedCallback(){this.stopAutomatic();this.events?.abort();this.controller?.abort();this.initialized=false;}
  }
  root.customElements.define('park-newsletter',ParkNewsletter);
})(typeof window==='undefined'?globalThis:window);

;

/* PARK shared UI source: assets/park-cart-terms.js */
(function () {
  'use strict';
  function createTermsFlow(ui) {
    let pending = null;
    return {
      request(context) {
        if (!context.checkbox || context.checkbox.checked) { ui.proceed(context); return; }
        pending = context;
        ui.open(context);
      },
      accept() {
        if (!pending) return;
        const context = pending;
        pending = null;
        context.checkbox.checked = true;
        ui.change(context.checkbox);
        ui.close(context);
        ui.proceed(context);
      },
      cancel() {
        if (!pending) return;
        const context = pending;
        pending = null;
        ui.close(context);
      }
    };
  }
  if (typeof module !== 'undefined') module.exports = { createTermsFlow };
  if (typeof document === 'undefined') return;
  const dialog = document.getElementById('park-checkout-terms');
  if (!dialog) return;
  // One explicit acceptance per browser-tab session, shared by both cart surfaces.
  const acceptanceKey = 'park:cart-terms:accepted:v1';
  let accepted = false;
  function restoreAcceptance() {
    try { accepted = window.sessionStorage.getItem(acceptanceKey) === 'true'; } catch (_) { /* Keep the in-page state when storage is unavailable. */ }
  }
  function syncTerms(root = document) {
    if (root.matches?.('[data-park-terms]')) root.checked = accepted;
    root.querySelectorAll?.('[data-park-terms]').forEach(checkbox => { checkbox.checked = accepted; });
  }
  restoreAcceptance();
  syncTerms();
  document.addEventListener('change', function (event) {
    if (!event.target.matches('[data-park-terms]')) return;
    accepted = event.target.checked;
    try {
      if (accepted) window.sessionStorage.setItem(acceptanceKey, 'true');
      else window.sessionStorage.removeItem(acceptanceKey);
    } catch (_) { /* Both visible carts still stay in sync. */ }
    syncTerms();
  });
  // Cart quantity changes replace sections without reloading this script.
  new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === 1) syncTerms(node);
    }));
  }).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('pageshow', function () { restoreAcceptance(); syncTerms(); });
  let bypass = false;
  const flow = createTermsFlow({
    open() { if (!dialog.open) dialog.showModal(); },
    close(context) { dialog.close(); if (context.button && context.button.isConnected) context.button.focus(); },
    change(checkbox) { checkbox.dispatchEvent(new Event('change', { bubbles: true })); },
    proceed(context) {
      // A cart refresh may replace or lock the checkout control while the popup is open.
      if (!context.button?.isConnected || context.button.getAttribute('aria-disabled') === 'true') return;
      if (context.url) { window.location.assign(context.url); return; }
      if (context.form && context.button && context.button.isConnected) {
        bypass = true;
        try { context.form.requestSubmit(context.button); } finally { bypass = false; }
      }
    }
  });
  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-park-checkout]');
    if (!button || bypass) return;
    const scope = button.closest('form, [data-park-terms-scope]');
    const checkbox = scope && scope.querySelector('[data-park-terms]');
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.getAttribute('aria-disabled') === 'true') return;
    flow.request({ button, checkbox, form: button.closest('form'), url: button.dataset.checkoutUrl });
  }, true);
  document.addEventListener('submit', function (event) {
    if (bypass || !event.target.matches('form.cart-form')) return;
    const button = event.submitter;
    if (!button || !button.matches('[data-park-checkout]')) return;
    const checkbox = event.target.querySelector('[data-park-terms]');
    if (checkbox && !checkbox.checked) {
      event.preventDefault(); event.stopImmediatePropagation();
      flow.request({ button, checkbox, form: event.target });
    }
  }, true);
  dialog.querySelector('[data-park-accept]').addEventListener('click', () => flow.accept());
  dialog.querySelector('[data-park-cancel]').addEventListener('click', () => flow.cancel());
  dialog.addEventListener('cancel', function (event) { event.preventDefault(); flow.cancel(); });
  dialog.addEventListener('click', function (event) { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) flow.cancel(); } });
})();
